import React, {useEffect, useState, useRef} from "react";
import {BeatLoader} from "react-spinners";
import {toast} from "react-toastify";
import {useStateValue} from "../../../states/StateProvider";
import {AES, enc} from "crypto-js";
import {useNavigate} from "react-router";
import {dbConnectionAPI, initializeiFExAuth, libreOfficeSetupAPI} from "../../../Api/ifex";
import {FiPlus, FiEdit2, FiTrash2, FiDatabase, FiCheckCircle, FiXCircle, FiAlertCircle, FiDownload, FiRefreshCw, FiGitCommit, FiCheck} from "react-icons/fi";
import DatabaseConnectionModal from "./DatabaseConnectionModal";
import ConfirmationModal from "../../../components/modal/ConfirmationModal";

const Settings = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [{secretKey, portal}, dispatch] = useStateValue();
    const navigate = useNavigate();

    // Connections state
    const [connections, setConnections] = useState([]);
    const [connectionsLoading, setConnectionsLoading] = useState(false);

    const normalizeConnection = (rawConnection = {}) => ({
        ...rawConnection,
        name: rawConnection.name ?? rawConnection.connection_name ?? '',
        host: rawConnection.host ?? rawConnection.server ?? '',
        driver: rawConnection.driver ?? 'sqlsrv',
        port: rawConnection.port ?? '',
        is_active: rawConnection.is_active ?? true,
    });

    // Modal state
    const [showConnectionModal, setShowConnectionModal] = useState(false);
    const [editingConnection, setEditingConnection] = useState(null);

    // Delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingConnection, setDeletingConnection] = useState(null);

    // Test connection state
    const [testingConnectionId, setTestingConnectionId] = useState(null);

    // LibreOffice Setup state
    const [libreOfficeStatus, setLibreOfficeStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [setupRunning, setSetupRunning] = useState(false);
    const [setupOutput, setSetupOutput] = useState([]);
    const [setupProgress, setSetupProgress] = useState('');
    const [showOutput, setShowOutput] = useState(false);
    const [gitOperationRunning, setGitOperationRunning] = useState(false);
    const outputEndRef = useRef(null);

    useEffect(() => {
        try {
            const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
            const userData = JSON.parse(decryptedBytes.toString(enc.Utf8));

            if (!userData) {
                navigate('/anmeldung');
                return;
            }

            // Check if user is Super Admin
            const isSuperAdmin = userData.isSAdmin === '1' || userData.role === 'Super Admin';
            if (!isSuperAdmin) {
                toast.error('Zugriff verweigert: Nur Super-Admins können auf diese Seite zugreifen');
                navigate('/');
                return;
            }

            setUser(userData);
            setLoading(false);
        } catch (e) {
            console.error('Authentication error:', e);
            window.location.replace('/anmeldung');
        }
    }, [secretKey, navigate]);

    useEffect(() => {
        if (user) {
            fetchConnections();
            checkLibreOfficeStatus();
        }
    }, [user]);

    // Auto-scroll to bottom of output
    useEffect(() => {
        if (outputEndRef.current && showOutput) {
            outputEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [setupOutput, showOutput]);

    const fetchConnections = async () => {
        setConnectionsLoading(true);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setConnectionsLoading(false);
                return;
            }

            const response = await dbConnectionAPI.getAll();
            const apiConnections = Array.isArray(response.data?.data) ? response.data.data : [];
            setConnections(apiConnections.map(normalizeConnection));
        } catch (error) {
            console.error('Error fetching connections:', error);
            toast.error('Fehler beim Laden der Verbindungen');
        } finally {
            setConnectionsLoading(false);
        }
    };

    const handleAddConnection = () => {
        setEditingConnection(null);
        setShowConnectionModal(true);
    };

    const handleEditConnection = (connection) => {
        setEditingConnection(connection);
        setShowConnectionModal(true);
    };

    const handleDeleteClick = (connection) => {
        setDeletingConnection(connection);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingConnection) return;

        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await dbConnectionAPI.delete(deletingConnection.id);
            toast.success(response.data?.message || 'Verbindung erfolgreich gelöscht');
            fetchConnections();
        } catch (error) {
            console.error('Error deleting connection:', error);
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error('Fehler beim Löschen der Verbindung');
            }
        } finally {
            setShowDeleteConfirm(false);
            setDeletingConnection(null);
        }
    };

    const handleTestConnection = async (connection) => {
        setTestingConnectionId(connection.id);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setTestingConnectionId(null);
                return;
            }

            const response = await dbConnectionAPI.testSaved(connection.id);

            if (response.data.success) {
                toast.success('Verbindung erfolgreich getestet!');
            } else {
                toast.error(response.data.message || 'Verbindungstest fehlgeschlagen');
            }
        } catch (error) {
            console.error('Error testing connection:', error);
            if (error.response?.data?.message) {
                toast.error(`Verbindungsfehler: ${error.response.data.message}`);
            } else {
                toast.error('Fehler beim Testen der Verbindung');
            }
        } finally {
            setTestingConnectionId(null);
        }
    };

    const handleModalSuccess = () => {
        setShowConnectionModal(false);
        setEditingConnection(null);
        fetchConnections();
    };

    // LibreOffice Setup Functions
    const checkLibreOfficeStatus = async () => {
        setStatusLoading(true);
        try {
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setStatusLoading(false);
                return;
            }

            const response = await libreOfficeSetupAPI.checkStatus();
            setLibreOfficeStatus(response.data);
        } catch (error) {
            console.error('Error checking LibreOffice status:', error);
            toast.error('Fehler beim Abrufen des LibreOffice-Status');
        } finally {
            setStatusLoading(false);
        }
    };

    const handleCleanup = async () => {
        if (!window.confirm('Möchten Sie LibreOffice wirklich deinstallieren? Alle Dateien werden gelöscht.')) {
            return;
        }

        try {
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await libreOfficeSetupAPI.cleanup();
            if (response.data.success) {
                toast.success('LibreOffice erfolgreich deinstalliert');
                checkLibreOfficeStatus();
                setSetupOutput([]);
                setShowOutput(false);
            }
        } catch (error) {
            console.error('Error cleaning up LibreOffice:', error);
            toast.error('Fehler beim Deinstallieren von LibreOffice');
        }
    };

    const handleRunSetup = async () => {
        setSetupRunning(true);
        setSetupOutput([]);
        setSetupProgress('Vorbereitung...');
        setShowOutput(true);

        try {
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setSetupRunning(false);
                return;
            }

            const baseURL = process.env.REACT_APP_IFEX_API_URL || 'http://localhost:8002/api';
            const token = localStorage.getItem('ifex_token');
            const url = `${baseURL}/libreoffice-setup/run`;

            const eventSource = new EventSource(`${url}?token=${token}`);

            eventSource.addEventListener('start', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'info', message: data.message }]);
                setSetupProgress('Wird gestartet...');
            });

            eventSource.addEventListener('output', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'output', message: data.data }]);
            });

            eventSource.addEventListener('error', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'error', message: data.data }]);
            });

            eventSource.addEventListener('success', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'success', message: data.message }]);
                setSetupProgress('Abgeschlossen');
                toast.success('LibreOffice erfolgreich installiert!');
                eventSource.close();
                setSetupRunning(false);
                checkLibreOfficeStatus();
            });

            eventSource.addEventListener('failed', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'error', message: data.message + (data.error ? `: ${data.error}` : '') }]);
                setSetupProgress('Fehlgeschlagen');
                toast.error('LibreOffice-Installation fehlgeschlagen');
                eventSource.close();
                setSetupRunning(false);
            });

            eventSource.onerror = () => {
                eventSource.close();
                setSetupRunning(false);
                if (setupProgress !== 'Abgeschlossen' && setupProgress !== 'Fehlgeschlagen') {
                    toast.error('Verbindung zum Server unterbrochen');
                    setSetupProgress('Unterbrochen');
                }
            };
        } catch (error) {
            console.error('Error running setup:', error);
            toast.error('Fehler beim Starten der Installation');
            setSetupRunning(false);
        }
    };

    const handleCommitAndPush = async () => {
        setGitOperationRunning(true);
        setSetupOutput(prev => [...prev, { type: 'info', message: '--- Git-Operationen starten ---' }]);

        try {
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setGitOperationRunning(false);
                return;
            }

            const baseURL = process.env.REACT_APP_IFEX_API_URL || 'http://localhost:8002/api';
            const token = localStorage.getItem('ifex_token');
            const url = `${baseURL}/libreoffice-setup/commit-push?token=${token}`;

            const eventSource = new EventSource(url);

            eventSource.addEventListener('start', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'info', message: data.message }]);
            });

            eventSource.addEventListener('output', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'output', message: data.data }]);
            });

            eventSource.addEventListener('success', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'success', message: data.message }]);
                toast.success('Änderungen erfolgreich committed und gepusht!');
                eventSource.close();
                setGitOperationRunning(false);
            });

            eventSource.addEventListener('failed', (e) => {
                const data = JSON.parse(e.data);
                setSetupOutput(prev => [...prev, { type: 'error', message: data.message + (data.error ? `: ${data.error}` : '') }]);
                toast.error('Git-Operation fehlgeschlagen');
                eventSource.close();
                setGitOperationRunning(false);
            });

            eventSource.onerror = () => {
                eventSource.close();
                setGitOperationRunning(false);
                toast.error('Verbindung zum Server unterbrochen');
            };
        } catch (error) {
            console.error('Error during git operation:', error);
            toast.error('Fehler bei Git-Operationen');
            setGitOperationRunning(false);
        }
    };

    if (loading) {
        return (
            <div className='dashboardContainer flex justify-center items-center'>
                <BeatLoader color="#1e3a8a" size={15} />
            </div>
        );
    }

    return (
        <div className='dashboardContainer'>
            <div className='flex justify-between items-center content-center pb-5'>
                <h2 className='text-2xl font-bold text-text text-left'>Einstellungen</h2>
            </div>

            {/* Database Connections Section */}
            <div className="bg-white rounded-lg shadow-lg border border-borderBlue mb-6">
                <div className="p-6 border-b border-borderBlue flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <FiDatabase className="text-mainBlue" size={24} />
                        <div>
                            <h3 className="text-lg font-semibold text-text">Datenbankverbindungen</h3>
                            <p className="text-sm text-grey">Verwaltung der externen Datenbankverbindungen</p>
                        </div>
                    </div>
                    <button
                        onClick={handleAddConnection}
                        className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
                    >
                        <FiPlus className="mr-2" />
                        <span>Neue Verbindung</span>
                    </button>
                </div>

                <div className="p-6">
                    {connectionsLoading ? (
                        <div className="text-center py-8">
                            <BeatLoader color="#3A46A9" size={12} />
                        </div>
                    ) : connections.length === 0 ? (
                        <div className="text-center py-16 bg-offWhite rounded">
                            <div className='inline-flex items-center justify-center w-16 h-16 bg-mainBlue bg-opacity-10 rounded-full mb-4'>
                                <FiAlertCircle className="h-8 w-8 text-mainBlue" />
                            </div>
                            <h3 className='text-lg font-semibold text-text mb-2'>Keine Datenbankverbindungen konfiguriert</h3>
                            <p className='text-base text-grey mb-6'>
                                Erstellen Sie Ihre erste Verbindung, um externe Datenquellen zu integrieren.
                            </p>
                            <button
                                onClick={handleAddConnection}
                                className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
                            >
                                Erste Verbindung erstellen
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full" style={{fontSize: "14px"}}>
                                <thead>
                                    <tr className="border-y border-silver">
                                        <th className="text-left py-2 text-text font-semibold pr-3">
                                            Name
                                        </th>
                                        <th className="text-left py-2 text-text font-semibold pr-3">
                                            Typ
                                        </th>
                                        <th className="text-left py-2 text-text font-semibold pr-3">
                                            Host
                                        </th>
                                        <th className="text-left py-2 text-text font-semibold pr-3">
                                            Datenbank
                                        </th>
                                        <th className="text-left py-2 text-text font-semibold pr-3">
                                            Benutzername
                                        </th>
                                        <th className="text-left py-2 text-text font-semibold pr-3">
                                            Status
                                        </th>
                                        <th className="text-left py-2 text-text font-semibold pr-3">
                                            Aktionen
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {connections.map((connection) => (
                                        <tr key={connection.id} className="border-y border-silver hover:bg-offWhite transition">
                                            <td className="py-2 font-light pr-3">
                                                <div className="flex items-center">
                                                    <FiDatabase className="text-mainBlue mr-2" />
                                                    <span className="text-sm font-medium text-text">
                                                        {connection.name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-2 text-text font-light pr-3">
                                                <span className="uppercase font-mono text-xs bg-silver px-2 py-1 rounded">
                                                    {connection.driver}
                                                </span>
                                            </td>
                                            <td className="py-2 text-text font-light pr-3">
                                                {connection.port ? `${connection.host}:${connection.port}` : connection.host}
                                            </td>
                                            <td className="py-2 text-text font-light pr-3">
                                                {connection.database}
                                            </td>
                                            <td className="py-2 text-text font-light pr-3">
                                                {connection.username}
                                            </td>
                                            <td className="py-2 font-light pr-3">
                                                {connection.is_active ? (
                                                    <span className="px-2 py-1 rounded text-xs bg-complete text-green">
                                                        Aktiv
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-1 rounded text-xs bg-redLight text-red">
                                                        Inaktiv
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2 font-light pr-3">
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={() => handleTestConnection(connection)}
                                                        disabled={testingConnectionId === connection.id}
                                                        className="text-green hover:text-opacity-80 disabled:opacity-50 transition"
                                                        title="Verbindung testen"
                                                    >
                                                        {testingConnectionId === connection.id ? (
                                                            <BeatLoader size={6} color="#20A100" />
                                                        ) : (
                                                            <FiCheckCircle size={18} />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditConnection(connection)}
                                                        className="text-mainBlue hover:text-lightBlue transition"
                                                        title="Bearbeiten"
                                                    >
                                                        <FiEdit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(connection)}
                                                        className="text-cancel hover:text-red transition"
                                                        title="Löschen"
                                                    >
                                                        <FiTrash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* LibreOffice Setup Section */}
            <div className="bg-white rounded-lg shadow-lg border border-borderBlue mb-6">
                <div className="p-6 border-b border-borderBlue flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <FiDownload className="text-mainBlue" size={24} />
                        <div>
                            <h3 className="text-lg font-semibold text-text">LibreOffice Installation</h3>
                            <p className="text-sm text-grey">PDF-Konvertierungssystem verwalten</p>
                        </div>
                    </div>
                    <button
                        onClick={checkLibreOfficeStatus}
                        disabled={statusLoading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-semibold rounded-lg text-text bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all disabled:opacity-50"
                    >
                        {statusLoading ? <BeatLoader size={8} color="#3A46A9" /> : <FiRefreshCw className="mr-2" />}
                        <span>Status aktualisieren</span>
                    </button>
                </div>

                <div className="p-6">
                    {/* Status Display */}
                    {statusLoading ? (
                        <div className="text-center py-8">
                            <BeatLoader color="#3A46A9" size={12} />
                        </div>
                    ) : libreOfficeStatus ? (
                        <div className="space-y-4">
                            {/* Installation Status */}
                            <div className="flex items-start gap-3 p-4 rounded-lg" style={{backgroundColor: libreOfficeStatus.installed ? '#E7F6EC' : '#FEF2F2'}}>
                                {libreOfficeStatus.installed ? (
                                    <FiCheckCircle className="text-green mt-1" size={24} />
                                ) : (
                                    <FiXCircle className="text-red mt-1" size={24} />
                                )}
                                <div className="flex-1">
                                    <h4 className="font-semibold text-text mb-1">
                                        {libreOfficeStatus.installed ? 'LibreOffice ist installiert' : 'LibreOffice ist nicht installiert'}
                                    </h4>
                                    {libreOfficeStatus.installed && (
                                        <div className="text-sm text-grey space-y-1">
                                            <p><strong>Version:</strong> {libreOfficeStatus.version || 'Unbekannt'}</p>
                                            <p className="font-mono text-xs">{libreOfficeStatus.path}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-3">
                                {!libreOfficeStatus.installed ? (
                                    <button
                                        onClick={handleRunSetup}
                                        disabled={setupRunning}
                                        className="inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green transition-all disabled:opacity-50"
                                    >
                                        {setupRunning ? (
                                            <>
                                                <BeatLoader size={8} color="#ffffff" className="mr-2" />
                                                <span>Installation läuft...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FiDownload className="mr-2" />
                                                <span>LibreOffice installieren</span>
                                            </>
                                        )}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            onClick={handleRunSetup}
                                            disabled={setupRunning}
                                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all disabled:opacity-50"
                                        >
                                            {setupRunning ? (
                                                <>
                                                    <BeatLoader size={8} color="#ffffff" className="mr-2" />
                                                    <span>Neuinstallation läuft...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiRefreshCw className="mr-2" />
                                                    <span>Neuinstallieren</span>
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={handleCleanup}
                                            disabled={setupRunning || gitOperationRunning}
                                            className="inline-flex items-center px-4 py-2 border border-red shadow-md text-sm font-semibold rounded-lg text-red bg-white hover:bg-red hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red transition-all disabled:opacity-50"
                                        >
                                            <FiTrash2 className="mr-2" />
                                            <span>Deinstallieren</span>
                                        </button>
                                        <button
                                            onClick={handleCommitAndPush}
                                            disabled={setupRunning || gitOperationRunning}
                                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-purple hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple transition-all disabled:opacity-50"
                                            style={{backgroundColor: gitOperationRunning ? '#9333EA' : '#7C3AED'}}
                                        >
                                            {gitOperationRunning ? (
                                                <>
                                                    <BeatLoader size={8} color="#ffffff" className="mr-2" />
                                                    <span>Git-Push läuft...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiGitCommit className="mr-2" />
                                                    <span>Commit & Push to Git</span>
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                                {setupOutput.length > 0 && (
                                    <button
                                        onClick={() => setShowOutput(!showOutput)}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-semibold rounded-lg text-text bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all"
                                    >
                                        {showOutput ? 'Output verbergen' : 'Output anzeigen'}
                                    </button>
                                )}
                            </div>

                            {/* Setup Progress */}
                            {setupProgress && (setupRunning || setupProgress === 'Abgeschlossen' || setupProgress === 'Fehlgeschlagen') && (
                                <div className="mt-4 p-3 rounded-lg bg-gray-100">
                                    <div className="flex items-center gap-2">
                                        {setupRunning && <BeatLoader size={8} color="#3A46A9" />}
                                        <span className="text-sm font-medium text-text">Status: {setupProgress}</span>
                                    </div>
                                </div>
                            )}

                            {/* Output Stream Display */}
                            {showOutput && setupOutput.length > 0 && (
                                <div className="mt-4">
                                    <div className="bg-gray-900 text-white p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto">
                                        {setupOutput.map((line, index) => (
                                            <div
                                                key={index}
                                                className={`mb-1 ${
                                                    line.type === 'error' ? 'text-red-400' :
                                                    line.type === 'success' ? 'text-green-400' :
                                                    line.type === 'info' ? 'text-blue-400' :
                                                    'text-gray-300'
                                                }`}
                                            >
                                                {line.message}
                                            </div>
                                        ))}
                                        <div ref={outputEndRef} />
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-grey">
                            Klicken Sie auf "Status aktualisieren", um den Installationsstatus zu überprüfen.
                        </div>
                    )}
                </div>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-silver border border-borderBlue rounded">
                <div className="flex items-start gap-2">
                    <FiAlertCircle className="text-mainBlue mt-0.5 flex-shrink-0" size={20} />
                    <div className="text-sm text-text">
                        <p className="font-semibold mb-1">Sicherheitshinweis:</p>
                        <p className="text-grey">
                            Passwörter werden verschlüsselt in der Datenbank gespeichert.
                            Stellen Sie sicher, dass nur autorisierte Benutzer Zugriff auf diese Verbindungen haben.
                        </p>
                    </div>
                </div>
            </div>

            {/* Connection Modal */}
            {showConnectionModal && (
                <DatabaseConnectionModal
                    visible={showConnectionModal}
                    connection={editingConnection}
                    onClose={() => {
                        setShowConnectionModal(false);
                        setEditingConnection(null);
                    }}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <ConfirmationModal
                    visible={showDeleteConfirm}
                    title="Verbindung löschen"
                    message={`Möchten Sie die Verbindung "${deletingConnection?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => {
                        setShowDeleteConfirm(false);
                        setDeletingConnection(null);
                    }}
                />
            )}
        </div>
    );
};

export default Settings;
